import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from '@/components/ui/separator';
import { SSOButtonGroup } from '@/components/auth/SSOButtonGroup';
import { CollapsibleEmailLoginForm } from '@/components/auth/CollapsibleEmailLoginForm';
import { LoadingSpinner } from '@/components/auth/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { AuthError } from '@supabase/supabase-js';
import { useIsMobile } from '@/hooks/use-mobile';
import { cleanupAuthState } from '@/utils/authCleanup';
import { signInWithPhone, verifyOtp } from '@/services/auth.service';
import { BrandLogo } from '@/components/ui/brand-logo';

const Login = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    isLoading: authLoading
  } = useAuth();
  const {
    isSuperAdmin,
    isLoading: isAdminLoading
  } = useAdminStatus();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const isMobile = useIsMobile();

  // Let route protection handle all redirects to prevent loops

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError(null);

    try {
      console.log('Starting login process');
      // Note: reCAPTCHA has been removed from this application. Use invisible protections instead.

      // Use Supabase directly for login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data?.user) {
        console.log('Login successful, user data:', data.user);
        toast.success('Successfully logged in');
        
        // Navigate to dashboard and let route protection handle proper redirects
        navigate('/dashboard');
      }
    } catch (error) {
      const authError = error as AuthError;
      console.error('Login error:', authError);
      setLoginError(authError.message || 'An error occurred during login');
      toast.error(authError.message || 'An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setLoginError('Please enter your email address');
      toast.error('Please enter your email address');
      return;
    }
    setIsResetting(true);
    setLoginError(null);
    try {
      // Note: reCAPTCHA has been removed from this application. Use invisible protections instead.

      // Use our custom email function instead of Supabase's default
      const {
        data,
        error
      } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'password_reset',
          email
        }
      });
      if (error) throw error;
      toast.success('Password reset instructions have been sent to your email');
    } catch (error: any) {
      console.error('Reset password error:', error);
      setLoginError(error.message || 'An error occurred while requesting password reset');
      toast.error(error.message || 'An error occurred while requesting password reset');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'linkedin_oidc' | 'azure' | 'apple') => {
    try {
      setLoginError(null);
      
      // Log OAuth attempt for debugging  
      console.log(`Initiating ${provider} OAuth login with redirect to:`, `${window.location.origin}/dashboard`);
      console.log('Current URL:', window.location.href);
      console.log('OAuth Configuration Check:', {
        provider,
        redirectTo: `${window.location.origin}/dashboard`,
        currentOrigin: window.location.origin
      });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: { 
            prompt: 'select_account',
            access_type: 'offline' // Request refresh token
          }
        }
      });
      
      if (error) {
        console.error(`${provider} OAuth error:`, {
          message: error.message,
          status: error.status,
          details: error,
          url: window.location.href,
          origin: window.location.origin
        });
        throw error;
      }
      
      console.log(`${provider} OAuth login initiated successfully`);
    } catch (error) {
      const authError = error as AuthError;
      console.error(`Social login error for ${provider}:`, {
        message: authError?.message,
        status: authError?.status,
        name: authError?.name,
        details: authError
      });
      
      const msg = authError?.message || `An error occurred during ${provider} login`;
      let friendly = msg;
      
      if (/invalid_client|origin_mismatch|redirect_uri_mismatch/i.test(msg)) {
        friendly = `${provider} sign-in is not configured for this site URL. Please verify the OAuth configuration in Supabase Dashboard and ensure ${window.location.origin} is added to the authorized origins.`;
        console.error('OAuth Origin/Redirect Mismatch:', {
          provider,
          currentOrigin: window.location.origin,
          redirectUrl: `${window.location.origin}/dashboard`,
          error: authError
        });
      } else if (msg.includes('404') || authError?.status === 404) {
        friendly = `Authentication failed - ${provider} OAuth is not properly configured. Please verify the configuration in Supabase Dashboard.`;
        console.error('OAuth 404 Error - Configuration Issue:', {
          provider,
          redirectUrl: `${window.location.origin}/dashboard`,
          currentUrl: window.location.href,
          currentOrigin: window.location.origin,
          suggestedActions: [
            'Check Supabase Auth Provider configuration',
            'Verify OAuth provider settings (LinkedIn/Microsoft)',
            'Ensure redirect URLs match'
          ]
        });
      }
      
      setLoginError(friendly);
      toast.error(friendly);
    }
  };

  const handlePhoneLogin = async (phone: string) => {
    try {
      setLoginError(null);
      const { error } = await signInWithPhone(phone);
      if (error) throw error;
      
      setPhoneNumber(phone);
      setShowOTP(true);
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      console.error('Phone login error:', error);
      setLoginError(error.message || 'Failed to send verification code');
      toast.error(error.message || 'Failed to send verification code');
    }
  };

  const handleOTPVerify = async (token: string) => {
    try {
      setLoginError(null);
      const { error } = await verifyOtp(phoneNumber, token);
      if (error) throw error;
      
      toast.success('Successfully logged in');
      setShowOTP(false);
    } catch (error: any) {
      console.error('OTP verification error:', error);
      setLoginError(error.message || 'Invalid verification code');
      toast.error(error.message || 'Invalid verification code');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Show loading spinner while auth is loading
  if (authLoading) {
    return <LoadingSpinner />;
  }

  // Don't redirect here - let the route protection handle all redirects
  // This prevents redirect loops

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Button variant="ghost" size="sm" onClick={handleGoBack} className="absolute top-4 left-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        
        <div>
          <div className="flex justify-center">
            <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome back!
          </h2>
          <p className={`mt-2 text-center ${isMobile ? 'text-xs px-4 whitespace-nowrap' : 'text-gray-600'}`}>Access your account and continue your PEO journey.</p>
        </div>

        {loginError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{loginError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <SSOButtonGroup
            onGoogleClick={() => handleSocialLogin('google')}
            onLinkedInClick={() => handleSocialLogin('linkedin_oidc')}
            onMicrosoftClick={() => handleSocialLogin('azure')}
            onPhoneSubmit={handlePhoneLogin}
            onOTPSubmit={handleOTPVerify}
            disabled={isSubmitting}
            showOTP={showOTP}
            phoneNumber={phoneNumber}
            hideMicrosoft={true}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gradient-to-br from-white to-green-50 px-2 text-muted-foreground"></span>
            </div>
          </div>

          <CollapsibleEmailLoginForm
            email={email}
            password={password}
            isSubmitting={isSubmitting}
            isResetting={isResetting}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onSubmit={handleSubmit}
            onResetPassword={handleResetPassword}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
